import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, downloadPdf } from "@/lib/api";
import { formatFCFA, formatDate, currentMonth } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Wallet, FileDown, Receipt, FileText, AlertCircle, Pencil, ChevronsUpDown, Check } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import Pagination, { paginate } from "@/components/Pagination";
import { toast } from "sonner";

const METHODES = ["Espèces", "Virement", "Mobile Money", "Chèque", "Avance"];
const METHODE_COLORS = {
  "Espèces": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Virement": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Mobile Money": "bg-amber-100 text-amber-800 border-amber-200",
  "Chèque": "bg-blue-100 text-blue-800 border-blue-200",
  "Avance": "bg-violet-100 text-violet-800 border-violet-200",
};

export default function Paiements() {
  const [items, setItems] = useState([]);
  const [locataires, setLocataires] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [taxPct, setTaxPct] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("paiements");
  const [locComboOpen, setLocComboOpen] = useState(false);
  const [unpaidMois, setUnpaidMois] = useState(currentMonth());
  const [unpaidRows, setUnpaidRows] = useState([]);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [unpaidSearch, setUnpaidSearch] = useState("");
  const [unpaidPage, setUnpaidPage] = useState(1);
  const [form, setForm] = useState({
    locataire_id: "", maison_id: "", montant: 0, mois: currentMonth(),
    date_paiement: "", methode: "Espèces", reference: "", notes: "", sejour_days: 0, sejour_start_date: "",
  });

  const load = async () => {
    const [p, l, m, c] = await Promise.all([
      api.get("/paiements"),
      api.get("/locataires"),
      api.get("/maisons"),
      api.get("/configuration"),
    ]);
    setItems(p.data); setLocataires(l.data); setMaisons(m.data);
    setTaxPct(Number(c.data?.tax_habitation_pct || 0));
  };
  useEffect(() => { load(); }, []);

  const loadUnpaid = async (m) => {
    setUnpaidLoading(true);
    try {
      const res = await api.get(`/rapports/loyers-attendus?mois=${m}`);
      const rows = (res.data?.rows || []).filter((r) => r.solde > 0);
      setUnpaidRows(rows);
    } catch {
      toast.error("Impossible de charger les impayés");
      setUnpaidRows([]);
    } finally {
      setUnpaidLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "impayes") loadUnpaid(unpaidMois);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, unpaidMois]);

  const openPayForUnpaid = (row) => {
    setForm({
      locataire_id: row.locataire_id,
      maison_id: row.maison_id || "",
      montant: row.solde > 0 ? row.solde : row.attendu,
      mois: unpaidMois,
      date_paiement: new Date().toISOString().slice(0, 10),
      methode: "Espèces",
      reference: "",
      notes: "",
    });
    setOpen(true);
  };

  // Pre-fill from notification click: /paiements?locataire=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const lid = searchParams.get("locataire");
    if (lid && locataires.length) {
      const loc = locataires.find((x) => x.id === lid);
      if (loc) {
        setSearch(loc.nom);
        setForm((f) => ({
          ...f,
          locataire_id: loc.id,
          maison_id: loc.maison_id || "",
          montant: loc.loyer_convenu || 0,
        }));
        setOpen(true);
        // clear the param so refresh doesn't re-open
        searchParams.delete("locataire");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locataires]);

  const locName = (id) => locataires.find((x) => x.id === id)?.nom || "—";
  const maisonName = (id) => maisons.find((x) => x.id === id)?.nom || "—";

  const filteredItems = items.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (p.numero || "").toLowerCase().includes(q) ||
      (p.mois || "").toLowerCase().includes(q) ||
      (p.methode || "").toLowerCase().includes(q) ||
      (p.reference || "").toLowerCase().includes(q) ||
      (locName(p.locataire_id) || "").toLowerCase().includes(q) ||
      (maisonName(p.maison_id) || "").toLowerCase().includes(q)
    );
  });
  const pag = paginate(filteredItems, page);
  useEffect(() => { setPage(1); }, [search]);

  const onLocataireChange = (id) => {
    const loc = locataires.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      locataire_id: id,
      maison_id: loc?.maison_id || "",
      montant: loc?.loyer_convenu || f.montant,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.locataire_id || !form.maison_id) {
      toast.error("Locataire et maison requis"); return;
    }
    try {
      const payload = {
        ...form,
        montant: Number(form.montant || 0),
        date_paiement: form.date_paiement ? new Date(form.date_paiement).toISOString() : null,
        sejour_days: Number(form.sejour_days || 0) || null,
        sejour_start_date: form.sejour_start_date || null,
      };
      if (editingId) {
        await api.put(`/paiements/${editingId}`, payload);
      } else {
        await api.post("/paiements", payload);
      }
      toast.success(editingId ? "Paiement modifié" : "Paiement enregistré");
      setOpen(false);
      setEditingId(null);
      setForm({ locataire_id: "", maison_id: "", montant: 0, mois: currentMonth(), date_paiement: "", methode: "Espèces", reference: "", notes: "", sejour_days: 0, sejour_start_date: "" });
      load();
      if (tab === "impayes") loadUnpaid(unpaidMois);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const onEdit = (p) => {
    setEditingId(p.id);
    setForm({
      locataire_id: p.locataire_id || "",
      maison_id: p.maison_id || "",
      montant: Number(p.montant || 0),
      mois: p.mois || currentMonth(),
      date_paiement: p.date_paiement ? p.date_paiement.slice(0, 10) : "",
      methode: p.methode || "Espèces",
      reference: p.reference || "",
      notes: p.notes || "",
      sejour_days: p.sejour_days || 0,
      sejour_start_date: p.sejour_start_date || "",
    });
    setOpen(true);
  };

  const onDelete = async () => {
    try { await api.delete(`/paiements/${confirmId}`); toast.success("Paiement supprimé"); setConfirmId(null); load(); }
    catch { toast.error("Suppression échouée"); }
  };

  const monthItems = items.filter((p) => p.mois === currentMonth());
  const totalMonth = monthItems.reduce((s, x) => s + (x.montant || 0), 0);
  const taxMonth = monthItems.reduce((s, x) => {
    const loc = locataires.find((l) => l.id === x.locataire_id);
    if (loc && loc.tax_habitation_active === false) return s;
    return s + Math.round((x.montant || 0) * taxPct / 100);
  }, 0);
  const netMonth = totalMonth - taxMonth;

  const netOf = (montant, locataireId) => {
    const m = Number(montant || 0);
    const loc = locataires.find((x) => x.id === locataireId);
    if (loc && loc.tax_habitation_active === false) return m;
    return m - Math.round(m * taxPct / 100);
  };

  return (
    <div>
      <PageHeader
        title="Paiements"
        subtitle="Enregistrement manuel des loyers reçus, avec mois concerné et méthode de paiement."
        testid="page-paiements"
        accent="teal"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher paiement…" testid="search-paiements" />
            <Button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-paiement">
              <Plus className="w-4 h-4 mr-2" /> Enregistrer un paiement
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-slate-100" data-testid="paiements-tabs">
          <TabsTrigger value="paiements" data-testid="tab-paiements">Paiements</TabsTrigger>
          <TabsTrigger value="impayes" data-testid="tab-impayes" className="data-[state=active]:text-rose-700">
            <AlertCircle className="w-4 h-4 mr-1.5" /> Impayés
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paiements" className="mt-4">
          <div className={`grid grid-cols-1 ${taxPct > 0 ? "sm:grid-cols-3" : ""} gap-4 mb-6`}>
        <Card className="p-5 rounded-xl border border-teal-200 shadow-sm bg-gradient-to-br from-teal-50 to-white flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-teal-700">Brut encaissé sur {currentMonth()}</div>
            <div className="font-heading text-3xl font-bold text-teal-900 mt-1" data-testid="stat-brut-mois">{formatFCFA(totalMonth)}</div>
          </div>
          <Wallet className="w-10 h-10 text-teal-500/40" />
        </Card>
        {taxPct > 0 && (
          <>
            <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-gradient-to-br from-rose-50 to-white">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700">Taxe d'habitation ({taxPct}%)</div>
              <div className="font-heading text-3xl font-bold text-rose-800 mt-1" data-testid="stat-taxe-mois">− {formatFCFA(taxMonth)}</div>
            </Card>
            <Card className="p-5 rounded-xl border border-emerald-200 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">Net perçu sur {currentMonth()}</div>
              <div className="font-heading text-3xl font-bold text-emerald-800 mt-1" data-testid="stat-net-mois">{formatFCFA(netMonth)}</div>
            </Card>
          </>
        )}
      </div>

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">N° reçu</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mois</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataire</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Méthode</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Montant</TableHead>
              {taxPct > 0 && (
                <TableHead className="text-xs uppercase tracking-wider text-emerald-700 font-semibold text-right">Net perçu</TableHead>
              )}
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow><TableCell colSpan={taxPct > 0 ? 9 : 8} className="text-center text-slate-500 py-12">{search ? `Aucun résultat pour « ${search} ».` : "Aucun paiement enregistré."}</TableCell></TableRow>
            )}
            {pag.rows.map((p) => (
              <TableRow key={p.id} className="hover:bg-slate-50/60">
                <TableCell className="text-slate-600 font-mono text-xs">{p.numero || "—"}</TableCell>
                <TableCell className="text-slate-600">{formatDate(p.date_paiement)}</TableCell>
                <TableCell><Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50">{p.mois}</Badge></TableCell>
                <TableCell className="font-medium text-slate-900">{locName(p.locataire_id)}</TableCell>
                <TableCell className="text-slate-600">{maisonName(p.maison_id)}</TableCell>
                <TableCell className="text-slate-600">
                  <Badge className={`${METHODE_COLORS[p.methode] || "bg-slate-100 text-slate-700 border-slate-200"} border hover:opacity-90 font-normal`}>
                    {p.methode}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-medium text-emerald-700">{formatFCFA(p.montant)}</TableCell>
                {taxPct > 0 && (
                  <TableCell className="text-right font-semibold text-emerald-800" data-testid={`net-cell-${p.id}`}>{formatFCFA(netOf(p.montant, p.locataire_id))}</TableCell>
                )}
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`btn-download-${p.id}`} title="Télécharger">
                        <FileDown className="w-4 h-4 text-emerald-700" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">Télécharger le reçu</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => downloadPdf(`/paiements/${p.id}/pdf?format=a4`).catch(() => toast.error("Erreur téléchargement"))} data-testid={`dl-a4-${p.id}`}>
                        <FileText className="w-4 h-4 mr-2" /> Reçu A4 (PDF)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPdf(`/paiements/${p.id}/pdf?format=ticket`).catch(() => toast.error("Erreur téléchargement"))} data-testid={`dl-ticket-${p.id}`}>
                        <Receipt className="w-4 h-4 mr-2" /> Ticket 80 mm
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`btn-edit-${p.id}`} title="Modifier"><Pencil className="w-4 h-4 text-teal-700" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(p.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-paiements" />
        </TabsContent>

        <TabsContent value="impayes" className="mt-4">
          <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-gradient-to-br from-rose-50 to-white mb-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700 mb-1">Suivi des impayés</div>
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-600" /> Locataires avec solde restant
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Liste les locataires actifs dont le loyer du mois n'est pas totalement encaissé. Cliquez sur « Payer » pour enregistrer un règlement.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="w-56">
                  <Label className="text-xs">Rechercher</Label>
                  <Input
                    value={unpaidSearch}
                    onChange={(e) => { setUnpaidSearch(e.target.value); setUnpaidPage(1); }}
                    placeholder="Locataire ou maison…"
                    data-testid="input-unpaid-search"
                  />
                </div>
                <div className="w-40">
                  <Label className="text-xs">Mois (YYYY-MM)</Label>
                  <Input
                    type="month"
                    value={unpaidMois}
                    onChange={(e) => { setUnpaidMois(e.target.value); setUnpaidPage(1); }}
                    data-testid="input-unpaid-mois"
                  />
                </div>
              </div>
            </div>
          </Card>

          {(() => {
            const q = unpaidSearch.trim().toLowerCase();
            const filtered = q
              ? unpaidRows.filter((r) =>
                  (r.locataire_nom || "").toLowerCase().includes(q) ||
                  (r.maison_nom || "").toLowerCase().includes(q))
              : unpaidRows;
            const totalImpaye = filtered.reduce((s, r) => s + (r.solde || 0), 0);
            const pagU = paginate(filtered, unpaidPage);
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-white flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700">Impayés — {unpaidMois}</div>
                      <div className="font-heading text-3xl font-bold text-rose-800 mt-1" data-testid="stat-impayes-total">{formatFCFA(totalImpaye)}</div>
                    </div>
                    <AlertCircle className="w-10 h-10 text-rose-500/40" />
                  </Card>
                  <Card className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Locataires concernés</div>
                      <div className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="stat-impayes-count">{filtered.length}</div>
                    </div>
                    <Wallet className="w-10 h-10 text-slate-400/40" />
                  </Card>
                </div>

                <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataire</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Loyer attendu</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Déjà payé</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-rose-700 font-semibold text-right">Solde dû</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unpaidLoading && (
                        <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">Chargement…</TableCell></TableRow>
                      )}
                      {!unpaidLoading && filtered.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center text-emerald-700 py-12 font-medium">Aucun impayé pour {unpaidMois} 🎉</TableCell></TableRow>
                      )}
                      {!unpaidLoading && pagU.rows.map((r) => (
                        <TableRow key={r.locataire_id} className="hover:bg-rose-50/40" data-testid={`unpaid-row-${r.locataire_id}`}>
                          <TableCell className="font-medium text-slate-900">{r.locataire_nom}</TableCell>
                          <TableCell className="text-slate-600">{r.maison_nom}</TableCell>
                          <TableCell className="text-right text-slate-700">{formatFCFA(r.attendu)}</TableCell>
                          <TableCell className="text-right text-emerald-700">{formatFCFA(r.paye)}</TableCell>
                          <TableCell className="text-right font-semibold text-rose-700" data-testid={`unpaid-solde-${r.locataire_id}`}>{formatFCFA(r.solde)}</TableCell>
                          <TableCell>
                            <Badge className={r.statut === "partiel"
                              ? "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"
                              : "bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100"}>
                              {r.statut === "partiel" ? "Partiel" : "Impayé"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => openPayForUnpaid(r)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`btn-payer-${r.locataire_id}`}
                            >
                              <Wallet className="w-4 h-4 mr-1.5" /> Payer
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
                <Pagination {...pagU} onChange={setUnpaidPage} testid="pagination-impayes" />
              </>
            );
          })()}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setLocComboOpen(false); } }}>
        <DialogContent data-testid="dialog-paiement">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Modifier le paiement" : "Enregistrer un paiement"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Locataire *</Label>
                {(() => {
                  const activeLocs = locataires.filter((l) => l.statut === "actif");
                  const selected = activeLocs.find((l) => l.id === form.locataire_id);
                  const maisonNomOf = (l) => (maisons.find((m) => m.id === l.maison_id) || {}).nom || "";
                  return (
                    <Popover open={locComboOpen} onOpenChange={setLocComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={locComboOpen}
                          className="w-full justify-between font-normal h-10"
                          data-testid="combobox-paiement-loc"
                        >
                          <span className={selected ? "text-slate-900" : "text-slate-400"}>
                            {selected ? (
                              <>
                                {selected.nom}
                                {maisonNomOf(selected) && (
                                  <span className="text-slate-500"> — {maisonNomOf(selected)}</span>
                                )}
                              </>
                            ) : (
                              "Rechercher et choisir un locataire…"
                            )}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command
                          filter={(value, search) => {
                            const q = search.toLowerCase();
                            return value.toLowerCase().includes(q) ? 1 : 0;
                          }}
                        >
                          <CommandInput placeholder="Nom, téléphone ou maison…" data-testid="input-loc-search" />
                          <CommandList>
                            <CommandEmpty>Aucun locataire trouvé.</CommandEmpty>
                            <CommandGroup>
                              {activeLocs.map((l) => {
                                const maisonNom = maisonNomOf(l);
                                const searchable = [l.nom || "", l.telephone || "", maisonNom].join(" ");
                                return (
                                  <CommandItem
                                    key={l.id}
                                    value={`${searchable} ${l.id}`}
                                    onSelect={() => {
                                      onLocataireChange(l.id);
                                      setLocComboOpen(false);
                                    }}
                                    data-testid={`loc-option-${l.id}`}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${form.locataire_id === l.id ? "opacity-100 text-teal-600" : "opacity-0"}`} />
                                    <span className="flex-1">
                                      {l.nom}
                                      {maisonNom && <span className="text-slate-500"> — {maisonNom}</span>}
                                    </span>
                                    {l.telephone && <span className="text-xs text-slate-400">{l.telephone}</span>}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  );
                })()}
              </div>
              {(() => {
                const loc = locataires.find((x) => x.id === form.locataire_id);
                if (!loc?.is_temporary) return null;
                const todayIso = new Date().toISOString().slice(0, 10);
                const startIso = form.sejour_start_date || todayIso;
                const start = new Date(startIso + "T00:00:00");
                const days = Number(form.sejour_days || 0);
                const end = days > 0 ? new Date(start.getTime() + days * 86400000) : null;
                return (
                  <div className="col-span-2 rounded-lg px-4 py-3 border border-amber-200 bg-amber-50" data-testid="temp-stay-block">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.16em] font-bold text-amber-800">Locataire temporaire — durée du séjour</div>
                        <p className="text-xs text-slate-600 mt-0.5">Choisissez la date de début et la durée du séjour. Une notification sera envoyée à l'administrateur 24 h avant la fin.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[170px_130px_1fr] gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">Date de début</Label>
                        <Input
                          type="date"
                          value={form.sejour_start_date || todayIso}
                          onChange={(e) => setForm({ ...form, sejour_start_date: e.target.value })}
                          data-testid="input-sejour-start"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Jours</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={form.sejour_days || ""}
                          onChange={(e) => setForm({ ...form, sejour_days: Number(e.target.value || 0) })}
                          placeholder="Ex: 7"
                          data-testid="input-sejour-days"
                        />
                      </div>
                      <div className="text-sm text-slate-700 pb-1">
                        {end
                          ? <>Fin prévue :{" "}<span className="font-semibold text-amber-900" data-testid="sejour-end-date">{end.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span></>
                          : <span className="text-slate-500">Saisissez le nombre de jours pour voir la date de fin.</span>}
                      </div>
                    </div>
                    {(() => {
                      const rapidBtns = [1, 3, 7, 14, 30];
                      return (
                        <div className="flex gap-2 mt-2">
                          {rapidBtns.map((d) => (
                            <button
                              key={d}
                              type="button"
                              onClick={() => setForm({ ...form, sejour_days: d })}
                              className={`px-2.5 py-1 rounded text-xs font-medium border transition ${
                                form.sejour_days === d
                                  ? "bg-amber-500 border-amber-600 text-white"
                                  : "bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
                              }`}
                              data-testid={`btn-sejour-${d}`}
                            >
                              {d}j
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
              <div className="space-y-2 col-span-2">
                <Label>Maison *</Label>
                <Select value={form.maison_id} onValueChange={(v) => setForm({ ...form, maison_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Maison" /></SelectTrigger>
                  <SelectContent>
                    {maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mois concerné (YYYY-MM)</Label>
                <Input value={form.mois} onChange={(e) => setForm({ ...form, mois: e.target.value })} placeholder="2026-02" data-testid="input-paiement-mois" />
              </div>
              <div className="space-y-2">
                <Label>Date paiement</Label>
                <Input type="date" value={form.date_paiement} onChange={(e) => setForm({ ...form, date_paiement: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Montant (FCFA) *</Label>
                <Input type="number" min="0" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} required data-testid="input-paiement-montant" />
              </div>
              <div className="space-y-2">
                <Label>Méthode</Label>
                <Select value={form.methode} onValueChange={(v) => setForm({ ...form, methode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Référence</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="N° transaction, reçu, etc." />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              {form.locataire_id && form.methode === "Avance" && (() => {
                const loc = locataires.find((x) => x.id === form.locataire_id);
                const restante = loc?.avance_restante || 0;
                const montant = Number(form.montant || 0);
                const insufficient = montant > restante;
                return (
                  <div className={`col-span-2 rounded-lg px-4 py-3 border ${insufficient ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="avance-info">
                    <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-600 mb-1">Avance — déduction automatique</div>
                    <div className="text-sm text-slate-800">
                      Solde disponible : <span className="font-semibold text-emerald-800">{formatFCFA(restante)}</span>
                      {montant > 0 && (
                        <>
                          {" — Après ce paiement : "}
                          <span className={`font-semibold ${insufficient ? "text-rose-700" : "text-emerald-800"}`}>
                            {formatFCFA(Math.max(0, restante - montant))}
                          </span>
                        </>
                      )}
                    </div>
                    {insufficient && (
                      <div className="text-xs text-rose-700 mt-1 font-medium">Avance insuffisante. Ajoutez une avance depuis la page Locataires ou choisissez une autre méthode.</div>
                    )}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-paiement">{editingId ? "Enregistrer les modifications" : "Enregistrer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
